import {Input, Slider, Tooltip} from '@mui/material';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {allocationTypeDecimals} from '../../../utils/number.js';

/**
 * A Slider and a TextField displaying the slider's value
 * @param {SliderAndTextField.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function SliderAndTextField(props) {
    const [sliderValue, setSliderValue] = useState(props.allocateForValue?.value);
    const decimals = allocationTypeDecimals(props.allocationType);

    useEffect(() => {
        if ( props.allocateForValue) {
            setSliderValue(props.allocateForValue.value);
        }
    }, [props.allocateForValue?.value, props.allocateForValue]);

    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    const handleInputChange = (event) => {
        let value = event.target.value.replace(/[^0-9.]/g, '');

        if (value.includes('.')) {
            const parts = value.split('.');
            const integerPart = parts[0];
            let decimalPart = parts[1];

            if (decimalPart.length > decimals) {
                decimalPart = decimalPart.substring(0, decimals);
            }

            value = `${integerPart}.${decimalPart}`;
        }

        setSliderValue(value);
    };

    const handleBlur = (event) => {
        const value = event.target.value.replace(/[^0-9.]/g, '');
        let numericValue = value === '' ? 0 : parseFloat(value);

        if (isNaN(numericValue) || numericValue < props.min) {
            numericValue = props.min;
        } else if (numericValue > props.max) {
            numericValue = props.max;
        }


        const factor = Math.pow(10, decimals);
        numericValue = Math.round(numericValue * factor) / factor;

        setSliderValue(numericValue);
        props.handleAllocateForChange(numericValue, props.idx);
    };


    const getTooltipContent = () => {
        let percentage; let price; let quantity;
        switch (props.allocationType) {
        case 'percentage':
            percentage = sliderValue;
            price = (sliderValue / 100) * props.totalPrice;
            quantity = (sliderValue / 100) * props.totalQuantity;
            break;
        case 'price':
            percentage = (sliderValue / props.totalPrice) * 100;
            price = sliderValue;
            quantity = (sliderValue / props.totalPrice) * props.totalQuantity;
            break;
        case 'quantity':
            percentage = (sliderValue / props.totalQuantity) * 100;
            price = (sliderValue / props.totalQuantity) * props.totalPrice;
            quantity = sliderValue;
            break;
        default:
            percentage = sliderValue;
            price = sliderValue;
            quantity = sliderValue;
            break;
        }
        return (
            <>
                {`Percentage: ${Number(percentage).toFixed(allocationTypeDecimals('percentage'))}%`}
                <br />
                {`Amount: ${Number(price).toFixed(allocationTypeDecimals('price'))} ${props.currency}`}
                <br />
                {`Quantity: ${Number(quantity).toFixed(allocationTypeDecimals('quantity'))}`}
            </>
        );
    };

    return (
        <Tooltip disableFocusListener disableTouchListener title={props.sliderWrongAmountTooltipText}>
            <div className={'pr-editor-account-details-slider'}>
                <Slider
                    sx={{color: '#F19200'}}
                    value={typeof sliderValue === 'number' ? sliderValue : 0}
                    onChange={handleSliderChange}
                    onChangeCommitted={(e) => {
                        props.handleAllocateForChange(sliderValue, props.idx);
                    }}
                    valueLabelDisplay={'auto'}
                    min={props.min}
                    max={props.max}
                    disabled={props.disabled}
                />
                <Tooltip disableFocusListener disableTouchListener title={getTooltipContent()}>
                    <Input
                        sx={{
                            width: '210px',
                            fontWeight: props.allocateForValue?.isFixed ? 'bold' : 'normal',
                            color: props.allocationTotalError ? 'red' : 'inherit',
                        }}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        size={'small'}
                        value={sliderValue}
                        error={props.allocateForValue?.error}
                        disabled={props.disabled}
                        inputProps={{
                            'step': Math.pow(10, -1 * decimals),
                            'min': props.min,
                            'max': props.max,
                            'type': 'number',
                            'aria-labelledby': 'input-slider',
                        }}
                    /></Tooltip>
            </div>
        </Tooltip>
    );
}

SliderAndTextField.propTypes = {
    allocateForValue: PropTypes.object,
    handleAllocateForChange: PropTypes.func,
    idx: PropTypes.number,
    disabled: PropTypes.bool,
    sliderWrongAmountTooltipText: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    allocationType: PropTypes.oneOf(['percentage', 'price', 'quantity']),
    totalPrice: PropTypes.number,
    totalQuantity: PropTypes.number,
    currency: PropTypes.string,
    allocationTotalError: PropTypes.bool,
};
